import React, { useEffect, useRef, useState } from "react";
import { Modal, Spinner, Text, Banner } from "@shopify/polaris";
import Hls from "hls.js";
import axios from "../Assets/Lib/axios";

// The backend returns a playable URL for both shapes: a tokenised proxy URL for
// HLS, a signed CloudFront URL for progressive MP4. We never build one here —
// the stream proxy requires a signed token that only the server can mint, and
// the old guessable /api/stream/{id}/{file} form is being retired.
//
// HLS is identified by the proxy path rather than by ".m3u8", because the
// tokenised URL ends in the master filename but is routed through /api/stream.
const isHlsUrl = (url) =>
  typeof url === "string" && (url.includes("/api/stream/") || url.includes(".m3u8"));

function VideoPreviewModal({ playlist, open, onClose }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [source, setSource] = useState(null);
  const [error, setError] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  // Resolve the playable URL whenever the modal is opened for a playlist.
  useEffect(() => {
    let cancelled = false;

    if (!open || !playlist) {
      setSource(null);
      setError("");
      return undefined;
    }

    setIsResolving(true);
    setError("");
    setSource(null);

    axios
      .get(`/generate-video-link/${playlist.id}`)
      .then((result) => {
        if (cancelled) return;
        const url = result?.data?.data?.url;
        if (url) {
          setSource({ url, hls: isHlsUrl(url) });
        } else {
          setError("The server did not return a video URL for this playlist.");
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not generate a link for this video.");
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, playlist]);

  // Attach the resolved source to the <video> element.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !source) return undefined;

    if (!source.hls) {
      video.src = source.url;
    } else if (Hls.isSupported()) {
      // hls.js FIRST, native only as a fallback. Chrome and Edge answer "maybe"
      // to canPlayType('application/vnd.apple.mpegurl') but cannot actually play
      // HLS, so checking native support first hands them a src they will never
      // load and the player just sits there blank. Safari is the only engine
      // that both claims and delivers native HLS, and it lands in the branch
      // below because Hls.isSupported() is false there (no MSE).
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;

      hls.on(Hls.Events.ERROR, (_event, data) => {
        // Surface the real reason instead of a blank player — a silent failure
        // here is what made this bug so hard to pin down.
        console.error("hls.js error", data);

        if (!data.fatal) return;

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          setError(
            `Could not load the video stream (${data.details}).` +
              (data.response?.code ? ` HTTP ${data.response.code}.` : "")
          );
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          setError(`This video could not be played (${data.details}).`);
        }
      });

      hls.loadSource(source.url);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari.
      video.src = source.url;
    } else {
      setError("This browser cannot play HLS video.");
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute("src");
      video.load();
    };
  }, [source]);

  const title = playlist?.name
    ? `Preview: ${getFirstNonNullValue(playlist.name)}`
    : "Preview";

  return (
    <Modal open={open} onClose={onClose} title={title} large>
      <Modal.Section>
        {error ? (
          <div style={{ marginBottom: "1rem" }}>
            <Banner tone="critical">{error}</Banner>
          </div>
        ) : null}

        {/*
          The <video> stays mounted while resolving and the spinner sits on top
          of it. Swapping them out breaks playback: setSource() and
          setIsResolving(false) land in different microtasks (.then vs .finally),
          so React renders twice, and on the first of those the video element
          would not exist yet — the attach effect below would find a null ref,
          bail, and never re-run, because `source` does not change again.
        */}
        <div style={{ position: "relative" }}>
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            style={{
              width: "100%",
              maxHeight: "70vh",
              backgroundColor: "#000",
              borderRadius: "8px",
              display: "block",
            }}
          />

          {isResolving ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                backgroundColor: "rgba(0,0,0,0.65)",
                borderRadius: "8px",
              }}
            >
              <Spinner size="large" />
              <Text as="p" tone="subdued">
                Preparing the video…
              </Text>
            </div>
          ) : null}
        </div>

        {source && !error ? (
          <div style={{ marginTop: "0.75rem" }}>
            <Text as="p" tone="subdued" variant="bodySm">
              {source.hls
                ? "Adaptive stream (HLS) — link expires in 12 hours"
                : "Progressive video — signed link, expires in 3 hours"}
            </Text>
          </div>
        ) : null}
      </Modal.Section>
    </Modal>
  );

  function getFirstNonNullValue(obj) {
    if (typeof obj === "string") return obj;
    for (const value of Object.values(obj || {})) {
      if (value !== null) {
        return value;
      }
    }
    return null;
  }
}

export default VideoPreviewModal;
